import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Text } from '@fluentui/react-components';
import {
  GridRegular,
  BuildingRegular,
  TagRegular,
  PeopleRegular,
  SettingsRegular,
  DataBarVerticalRegular,
  ClipboardTextLtrRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { useAuth } from '@/auth/AuthContext';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: <GridRegular /> },
  { path: '/tenants', label: 'Tenants', icon: <BuildingRegular /> },
  { path: '/pricing', label: 'Pricing', icon: <TagRegular /> },
  { path: '/admins', label: 'Admin Users', icon: <PeopleRegular /> },
  { path: '/settings', label: 'Settings', icon: <SettingsRegular /> },
  { path: '/reports', label: 'Reports', icon: <DataBarVerticalRegular /> },
  { path: '/audit-log', label: 'Audit Log', icon: <ClipboardTextLtrRegular /> },
];

function getUserInitials(name?: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('')
    .slice(0, 2);
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userInitials = getUserInitials(user?.name);

  return (
    <div className="layout-container">
      <header className="layout-header">
        <button
          className="logo-button"
          onClick={() => navigate('/dashboard')}
          title="Go to dashboard"
        >
          <h1 className="logo">Sherpai</h1>
        </button>

        <div className="header-user">
          <div className="user-info">
            <Text weight="semibold" className="user-name">{user?.name}</Text>
            <Text size={200} className="user-email">
              {user?.email}
            </Text>
          </div>
          <div className="user-avatar">{userInitials}</div>
          <Button
            appearance="transparent"
            icon={<SignOutRegular />}
            onClick={handleLogout}
            title="Sign out"
            className="logout-button"
          >
            Logout
          </Button>
        </div>
      </header>

      <div className="layout-body">
        <aside className="layout-sidebar">
          <nav className="sidebar-nav">
            {navItems.map((item) => (
              <button
                key={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
                title={item.label}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="layout-main">{children}</main>
      </div>
    </div>
  );
}
